/**
 * Signal Intelligence Database
 * 
 * Rich metadata, historical context, and actionable insights for each signal.
 * This transforms raw data into professional-grade market intelligence.
 */

export const SIGNAL_INTELLIGENCE = {
  
  // ===========================================================================
  // LIQUIDITY SIGNALS
  // ===========================================================================
  
  NET_LIQUIDITY: {
    whyItMatters: `Net Liquidity is the single most important driver of risk asset prices in the post-2008 era. When the Fed's balance sheet expands faster than Treasury and repo drains, excess dollars flow into stocks, crypto, and credit. This metric predicted the 2020 rally, the 2022 drawdown, and every major inflection point in between.`,
    
    interpretation: [
      { level: 'low', range: '< $5.0T', meaning: 'Severe liquidity drought', implication: 'Risk assets face significant headwinds. Defensive positioning warranted.' },
      { level: 'normal', range: '$5.0T - $6.0T', meaning: 'Healthy liquidity conditions', implication: 'Supportive backdrop for risk assets. Normal market dynamics.' },
      { level: 'elevated', range: '$6.0T - $6.5T', meaning: 'Abundant liquidity', implication: 'Strong tailwind for equities and crypto. Risk-on environment.' },
      { level: 'high', range: '> $6.5T', meaning: 'Excess liquidity', implication: 'Potential bubble conditions. Watch for Fed tightening signals.' },
    ],
    
    historicalEvents: [
      { date: 'Mar 2020', value: '$3.2T', event: 'COVID crash bottom', description: 'Fed launched unlimited QE, liquidity surged $3T in weeks' },
      { date: 'Nov 2021', value: '$6.3T', event: 'Market top', description: 'Peak liquidity preceded 2022 bear market by 2 months' },
      { date: 'Oct 2022', value: '$5.1T', event: 'Bear market bottom', description: 'Liquidity trough aligned with S&P 500 lows' },
      { date: 'Mar 2023', value: '$5.4T', event: 'SVB crisis', description: 'Emergency lending temporarily boosted liquidity' },
      { date: 'Sep 2024', value: '$5.5T', event: 'QT continues', description: 'Fed balance sheet runoff maintains steady pace' },
      { date: 'Nov 2024', value: '$5.6T', event: 'Election uncertainty', description: 'Markets absorbed election volatility with stable liquidity' },
    ],
    
    correlations: [
      { signal: 'S&P 500', correlation: 0.89, leadLag: 'Coincident to leading by 2-4 weeks' },
      { signal: 'Bitcoin', correlation: 0.84, leadLag: 'Leading by 1-2 weeks' },
      { signal: 'High Yield Spreads', correlation: -0.72, leadLag: 'Inverse relationship' },
    ],
    
    playbook: [
      { level: 'low', actions: ['Reduce equity exposure', 'Raise cash levels', 'Consider Treasury allocation', 'Avoid high-beta assets'] },
      { level: 'normal', actions: ['Maintain balanced allocation', 'Monitor for directional shifts', 'Standard risk positioning'] },
      { level: 'elevated', actions: ['Overweight equities', 'Consider growth over value', 'Crypto allocation appropriate'] },
      { level: 'high', actions: ['Take profits on winners', 'Reduce leverage', 'Watch for Fed pivot signals'] },
    ],
    
    relatedSignals: ['LIQUIDITY_REGIME', 'FUNDING_STRESS_INDEX', 'SOFR_EFFR_SPREAD'],
  },

  LIQUIDITY_REGIME: {
    whyItMatters: `The Liquidity Regime synthesizes multiple Fed balance sheet components into a single actionable classification. It cuts through the noise of weekly fluctuations to identify the dominant liquidity trend. Regime changes have historically preceded major market turning points by 2-6 weeks.`,
    
    interpretation: [
      { level: 'tight', range: 'Z-score < -1', meaning: 'Liquidity contraction', implication: 'Risk-off environment. Cash and defensive assets outperform.' },
      { level: 'neutral', range: 'Z-score -1 to +1', meaning: 'Balanced conditions', implication: 'Mixed signals. Focus on stock/sector selection.' },
      { level: 'loose', range: 'Z-score > +1', meaning: 'Liquidity expansion', implication: 'Risk-on environment. High-beta outperforms.' },
    ],
    
    historicalEvents: [
      { date: 'Q4 2018', value: 'TIGHT', event: 'Fed tightening peak', description: 'Regime shift preceded 20% S&P correction' },
      { date: 'Q2 2020', value: 'LOOSE', event: 'Post-COVID expansion', description: 'Longest loose regime in history (18 months)' },
      { date: 'Q1 2022', value: 'TIGHT', event: 'QT begins', description: 'Regime shift marked start of bear market' },
    ],
    
    correlations: [
      { signal: 'NET_LIQUIDITY', correlation: 0.95, leadLag: 'Derived from Net Liquidity' },
      { signal: 'Risk Asset Performance', correlation: 0.78, leadLag: 'Leading by 2-6 weeks' },
    ],
    
    playbook: [
      { level: 'tight', actions: ['Underweight equities', 'Overweight cash/bonds', 'Avoid leverage', 'Focus on quality'] },
      { level: 'neutral', actions: ['Balanced allocation', 'Active management preferred', 'Selective risk-taking'] },
      { level: 'loose', actions: ['Overweight risk assets', 'Growth over value', 'Small caps attractive', 'Crypto allocation'] },
    ],
    
    relatedSignals: ['NET_LIQUIDITY', 'CURVE_2S10S', 'VIX_ZSCORE'],
  },

  // ===========================================================================
  // CREDIT SIGNALS
  // ===========================================================================

  HY_IG_SPREAD: {
    whyItMatters: `The spread between high-yield and investment-grade bonds is Wall Street's fear gauge for credit markets. When investors demand significantly more yield to hold junk bonds, it signals deteriorating confidence in corporate balance sheets. This spread has widened before every recession since 1990, typically 6-12 months in advance.`,
    
    interpretation: [
      { level: 'low', range: '< 2.0%', meaning: 'Complacency / Risk-on', implication: 'Credit markets healthy but potentially overconfident. Watch for reversals.' },
      { level: 'normal', range: '2.0% - 3.5%', meaning: 'Normal risk pricing', implication: 'Appropriate credit risk premium. Balanced conditions.' },
      { level: 'elevated', range: '3.5% - 4.5%', meaning: 'Stress emerging', implication: 'Credit concerns rising. Review HY exposure.' },
      { level: 'high', range: '4.5% - 6.0%', meaning: 'Significant stress', implication: 'Default expectations rising. Reduce credit risk.' },
      { level: 'extreme', range: '> 6.0%', meaning: 'Crisis conditions', implication: 'Severe credit crunch. Capital preservation mode.' },
    ],
    
    historicalEvents: [
      { date: 'Oct 2008', value: '8.4%', event: 'Lehman collapse', description: 'Widest spread in modern history during GFC' },
      { date: 'Feb 2016', value: '5.2%', event: 'Energy crisis', description: 'Oil crash stressed HY energy issuers' },
      { date: 'Mar 2020', value: '6.8%', event: 'COVID panic', description: 'Fastest spread widening on record' },
      { date: 'Oct 2022', value: '3.1%', event: 'Fed hiking cycle', description: 'Contained stress despite aggressive tightening' },
    ],
    
    correlations: [
      { signal: 'VIX', correlation: 0.76, leadLag: 'Coincident' },
      { signal: 'S&P 500', correlation: -0.68, leadLag: 'Inverse, leading by 1-2 weeks' },
      { signal: 'Default Rates', correlation: 0.82, leadLag: 'Leading by 6-9 months' },
    ],
    
    playbook: [
      { level: 'low', actions: ['Credit risk acceptable', 'HY allocation appropriate', 'Watch for complacency'] },
      { level: 'normal', actions: ['Maintain credit exposure', 'Favor higher quality HY', 'Monitor trend'] },
      { level: 'elevated', actions: ['Reduce HY exposure', 'Move up in quality', 'Add IG hedges'] },
      { level: 'high', actions: ['Minimal HY exposure', 'Investment grade only', 'Consider CDS protection'] },
      { level: 'extreme', actions: ['Exit HY completely', 'Treasuries and cash', 'Opportunistic buying only'] },
    ],
    
    relatedSignals: ['CCC_B_SPREAD', 'CREDIT_STRESS_INDEX', 'BBB_TREASURY_SPREAD'],
  },

  CCC_B_SPREAD: {
    whyItMatters: `The spread between CCC-rated (near junk) and B-rated bonds measures stress at the lowest end of the credit spectrum. This is where defaults happen first. Widening here often precedes broader credit stress by 2-4 months, making it an early warning system for credit market deterioration.`,
    
    interpretation: [
      { level: 'low', range: '< 4.0%', meaning: 'Aggressive risk appetite', implication: 'Investors reaching for yield. Late-cycle behavior.' },
      { level: 'normal', range: '4.0% - 6.0%', meaning: 'Normal differentiation', implication: 'Appropriate risk discrimination. Healthy market.' },
      { level: 'elevated', range: '6.0% - 8.0%', meaning: 'Quality rotation', implication: 'Investors fleeing lowest quality. Stress building.' },
      { level: 'high', range: '8.0% - 12.0%', meaning: 'Default fears', implication: 'CCC issuers losing market access. Defaults likely.' },
      { level: 'extreme', range: '> 12.0%', meaning: 'Credit market seizure', implication: 'Weakest issuers cannot refinance. Wave of defaults.' },
    ],
    
    historicalEvents: [
      { date: 'Dec 2008', value: '18.5%', event: 'GFC peak stress', description: 'CCC market effectively closed' },
      { date: 'Feb 2016', value: '11.2%', event: 'Energy distress', description: 'Shale producers facing bankruptcy' },
      { date: 'Mar 2020', value: '13.8%', event: 'COVID liquidity crisis', description: 'Brief but severe dislocation' },
    ],
    
    correlations: [
      { signal: 'HY_IG_SPREAD', correlation: 0.88, leadLag: 'Leading by 2-4 weeks' },
      { signal: 'Bankruptcy Filings', correlation: 0.79, leadLag: 'Leading by 3-6 months' },
    ],
    
    playbook: [
      { level: 'low', actions: ['Caution on CCC exposure', 'Late-cycle positioning', 'Take profits'] },
      { level: 'normal', actions: ['Selective CCC exposure OK', 'Focus on improving credits', 'Diversify'] },
      { level: 'elevated', actions: ['Exit CCC entirely', 'B-rated maximum', 'Raise quality'] },
      { level: 'high', actions: ['BB and above only', 'Avoid new issuance', 'Prepare for dislocations'] },
      { level: 'extreme', actions: ['Treasuries only', 'Watch for distressed opportunities', 'Patience'] },
    ],
    
    relatedSignals: ['HY_IG_SPREAD', 'CREDIT_STRESS_INDEX', 'FUNDING_STRESS_INDEX'],
  },

  BBB_TREASURY_SPREAD: {
    whyItMatters: `BBB-rated bonds sit just above junk status - the "fallen angel" threshold. This spread measures the premium investors require for the lowest investment-grade debt. When it widens, it signals concerns about potential downgrades flooding the high-yield market with forced sellers.`,
    
    interpretation: [
      { level: 'low', range: '< 1.0%', meaning: 'Tight spreads', implication: 'Strong confidence in corporate credit. Possible complacency.' },
      { level: 'normal', range: '1.0% - 1.8%', meaning: 'Fair value', implication: 'Appropriate risk premium. Normal conditions.' },
      { level: 'elevated', range: '1.8% - 2.5%', meaning: 'Caution emerging', implication: 'Downgrade concerns rising. Monitor closely.' },
      { level: 'high', range: '2.5% - 3.5%', meaning: 'Stress', implication: 'Fallen angel risk elevated. Reduce BBB exposure.' },
      { level: 'extreme', range: '> 3.5%', meaning: 'Crisis', implication: 'Mass downgrade fears. Flight to quality.' },
    ],
    
    historicalEvents: [
      { date: 'Mar 2020', value: '4.0%', event: 'COVID crisis', description: 'Fallen angel wave feared, Fed intervened' },
      { date: '2008-2009', value: '6.0%+', event: 'GFC', description: 'Massive downgrade cycle materialized' },
    ],
    
    correlations: [
      { signal: 'HY_IG_SPREAD', correlation: 0.82, leadLag: 'Coincident' },
      { signal: 'IG Fund Flows', correlation: -0.65, leadLag: 'Inverse relationship' },
    ],
    
    playbook: [
      { level: 'normal', actions: ['BBB allocation appropriate', 'Focus on stable issuers', 'Avoid leveraged names'] },
      { level: 'elevated', actions: ['Review BBB holdings', 'Favor A-rated', 'Reduce duration'] },
      { level: 'high', actions: ['Minimize BBB', 'Move to Treasuries', 'Watch for opportunities'] },
    ],
    
    relatedSignals: ['HY_IG_SPREAD', 'CREDIT_STRESS_INDEX'],
  },

  CREDIT_STRESS_INDEX: {
    whyItMatters: `The Credit Stress Index combines multiple credit market signals into a single composite score. It weighs spreads, volatility, and funding conditions to provide an overall read on credit market health. A rising index often precedes broader market stress by 2-4 weeks.`,
    
    interpretation: [
      { level: 'low', range: '< 25', meaning: 'Calm conditions', implication: 'Credit markets healthy and functioning well.' },
      { level: 'normal', range: '25 - 50', meaning: 'Normal stress', implication: 'Typical market volatility. No action needed.' },
      { level: 'elevated', range: '50 - 70', meaning: 'Rising stress', implication: 'Credit conditions tightening. Increase monitoring.' },
      { level: 'high', range: '70 - 85', meaning: 'High stress', implication: 'Significant credit deterioration. Defensive posture.' },
      { level: 'extreme', range: '> 85', meaning: 'Crisis', implication: 'Credit market dysfunction. Capital preservation.' },
    ],
    
    historicalEvents: [
      { date: 'Mar 2020', value: '94', event: 'COVID peak', description: 'Highest reading since 2008' },
      { date: 'Oct 2008', value: '98', event: 'GFC', description: 'All-time high stress reading' },
    ],
    
    playbook: [
      { level: 'low', actions: ['Full credit allocation', 'Carry strategies attractive', 'Complacency watch'] },
      { level: 'normal', actions: ['Standard allocation', 'Quality focus', 'Normal risk management'] },
      { level: 'elevated', actions: ['Reduce credit beta', 'Raise quality', 'Add hedges'] },
      { level: 'high', actions: ['Minimal credit risk', 'Safe havens', 'Cash reserves'] },
      { level: 'extreme', actions: ['Capital preservation', 'Treasuries/cash', 'Prepare for opportunities'] },
    ],
    
    relatedSignals: ['HY_IG_SPREAD', 'CCC_B_SPREAD', 'VIX_ZSCORE'],
  },

  // ===========================================================================
  // RATES SIGNALS
  // ===========================================================================

  CURVE_2S10S: {
    whyItMatters: `The 2s10s spread is the market's most-watched recession indicator. An inverted curve (negative spread) has preceded every US recession since 1955 with only one false positive. The signal typically leads recession by 12-18 months, giving investors time to prepare.`,
    
    interpretation: [
      { level: 'extreme', range: '< -0.5%', meaning: 'Deeply inverted', implication: 'Strong recession signal. Historically 85% accuracy within 18 months.' },
      { level: 'high', range: '-0.5% to 0%', meaning: 'Inverted', implication: 'Recession warning. Begin defensive positioning.' },
      { level: 'elevated', range: '0% to 0.5%', meaning: 'Flat', implication: 'Late cycle. Mixed signals. Heightened monitoring.' },
      { level: 'normal', range: '0.5% to 1.5%', meaning: 'Normal slope', implication: 'Healthy term premium. Economic expansion.' },
      { level: 'low', range: '> 1.5%', meaning: 'Steep curve', implication: 'Early cycle expansion. Risk-on environment.' },
    ],
    
    historicalEvents: [
      { date: 'Aug 2019', value: '-0.05%', event: 'Pre-COVID inversion', description: 'Brief inversion preceded COVID recession' },
      { date: 'Jul 2022', value: '-0.50%', event: 'Fed hiking cycle', description: 'Deepest inversion since 1980s' },
      { date: 'Dec 2006', value: '-0.15%', event: 'Pre-GFC', description: 'Inversion preceded 2008 crisis by 18 months' },
      { date: 'Mar 2000', value: '-0.52%', event: 'Dot-com peak', description: 'Inversion preceded 2001 recession' },
    ],
    
    correlations: [
      { signal: 'Recession (12-18mo forward)', correlation: 0.85, leadLag: 'Leading by 12-18 months' },
      { signal: 'Bank Profitability', correlation: 0.72, leadLag: 'Coincident' },
      { signal: 'Credit Availability', correlation: 0.68, leadLag: 'Leading by 6-12 months' },
    ],
    
    playbook: [
      { level: 'extreme', actions: ['Maximum defensive', 'Extend duration', 'Quality over growth', 'Reduce cyclicals'] },
      { level: 'high', actions: ['Underweight equities', 'Favor defensives', 'Build cash', 'Avoid leverage'] },
      { level: 'elevated', actions: ['Neutral positioning', 'Balanced allocation', 'Active management'] },
      { level: 'normal', actions: ['Standard allocation', 'Cyclicals appropriate', 'Normal risk-taking'] },
      { level: 'low', actions: ['Overweight risk', 'Cyclicals/small caps', 'Growth orientation'] },
    ],
    
    relatedSignals: ['CURVE_3M10Y', 'REAL_10Y', 'TERM_PREMIUM_PROXY'],
  },

  CURVE_3M10Y: {
    whyItMatters: `The 3-month to 10-year spread is the Fed's preferred recession indicator. Research by the New York Fed found this curve has the strongest predictive power for recessions, with inversions correctly forecasting downturns 8-14 months ahead.`,
    
    interpretation: [
      { level: 'extreme', range: '< -1.0%', meaning: 'Severely inverted', implication: 'Highest recession probability. Near certainty of slowdown.' },
      { level: 'high', range: '-1.0% to -0.25%', meaning: 'Inverted', implication: 'Recession odds >50%. Defensive positioning.' },
      { level: 'elevated', range: '-0.25% to 0.25%', meaning: 'Flat', implication: 'Transition period. Increased uncertainty.' },
      { level: 'normal', range: '0.25% to 2.0%', meaning: 'Normal', implication: 'Healthy curve. Expansion mode.' },
      { level: 'low', range: '> 2.0%', meaning: 'Steep', implication: 'Early recovery. Strong growth expectations.' },
    ],
    
    historicalEvents: [
      { date: 'Mar 2023', value: '-1.8%', event: 'Historic inversion', description: 'Deepest inversion in 40+ years' },
      { date: 'Aug 2006', value: '-0.3%', event: 'Pre-GFC', description: 'Inversion began 24 months before crisis' },
    ],
    
    playbook: [
      { level: 'extreme', actions: ['Recession preparation', 'Quality bonds', 'Defensive equities', 'Cash buffer'] },
      { level: 'high', actions: ['Reduce risk', 'Extend duration', 'Favor stable earnings'] },
      { level: 'normal', actions: ['Balanced approach', 'Full equity allocation', 'Normal duration'] },
    ],
    
    relatedSignals: ['CURVE_2S10S', 'TERM_PREMIUM_PROXY', 'REAL_10Y'],
  },

  REAL_10Y: {
    whyItMatters: `The real 10-year yield (nominal minus inflation expectations) represents the true cost of capital. Negative real yields are historically associated with strong risk asset performance and financial repression. Positive real yields above 2% signal restrictive conditions that pressure valuations.`,
    
    interpretation: [
      { level: 'low', range: '< 0%', meaning: 'Negative real yields', implication: 'Financial repression. Supportive for risk assets.' },
      { level: 'normal', range: '0% to 1.5%', meaning: 'Moderate real rates', implication: 'Balanced conditions. Normal valuations.' },
      { level: 'elevated', range: '1.5% to 2.5%', meaning: 'Restrictive', implication: 'Valuation pressure. Favor value over growth.' },
      { level: 'high', range: '> 2.5%', meaning: 'Very restrictive', implication: 'Significant headwind. Growth stocks vulnerable.' },
    ],
    
    historicalEvents: [
      { date: '2020-2021', value: '-1.0%', event: 'COVID era', description: 'Deepest negative real yields in history' },
      { date: 'Oct 2023', value: '2.5%', event: 'Normalization', description: 'First positive real yields since 2018' },
    ],
    
    playbook: [
      { level: 'low', actions: ['Overweight growth', 'Long duration equities OK', 'Real assets', 'Avoid cash drag'] },
      { level: 'normal', actions: ['Balanced style', 'Quality focus', 'Normal allocation'] },
      { level: 'elevated', actions: ['Rotate to value', 'Shorter duration', 'Profitable companies'] },
      { level: 'high', actions: ['Deep value focus', 'Cash flow priority', 'Reduce duration'] },
    ],
    
    relatedSignals: ['REAL_5Y', 'CURVE_2S10S', 'TERM_PREMIUM_PROXY'],
  },

  REAL_5Y: {
    whyItMatters: `The real 5-year yield captures medium-term real rate expectations, which directly impact mortgage rates, corporate borrowing costs, and private equity returns. It's particularly relevant for assessing housing market conditions and leveraged investment strategies.`,
    
    interpretation: [
      { level: 'low', range: '< 0%', meaning: 'Accommodative', implication: 'Cheap financing. Leverage strategies attractive.' },
      { level: 'normal', range: '0% to 1.5%', meaning: 'Neutral', implication: 'Normal borrowing conditions.' },
      { level: 'elevated', range: '1.5% to 2.5%', meaning: 'Tight', implication: 'Financing costs elevated. Deleverage pressure.' },
      { level: 'high', range: '> 2.5%', meaning: 'Restrictive', implication: 'Significant headwind to leveraged strategies.' },
    ],
    
    playbook: [
      { level: 'low', actions: ['Leverage appropriate', 'Real estate attractive', 'Growth stocks'] },
      { level: 'elevated', actions: ['Reduce leverage', 'Refinancing risk', 'Quality focus'] },
    ],
    
    relatedSignals: ['REAL_10Y', 'CURVE_2S10S'],
  },

  TERM_PREMIUM_PROXY: {
    whyItMatters: `The term premium represents the extra yield investors demand for holding long-term bonds vs rolling short-term bonds. A negative term premium suggests strong demand for duration (flight to safety) or Fed credibility concerns. Rising term premium often signals inflation worries or fiscal concerns.`,
    
    interpretation: [
      { level: 'low', range: '< -0.5%', meaning: 'Negative premium', implication: 'Unusual demand for duration. Safe haven flows.' },
      { level: 'normal', range: '-0.5% to 0.5%', meaning: 'Compressed', implication: 'QE effects or strong credibility. Bonds expensive.' },
      { level: 'elevated', range: '0.5% to 1.5%', meaning: 'Normal', implication: 'Appropriate duration compensation.' },
      { level: 'high', range: '> 1.5%', meaning: 'Elevated', implication: 'Duration risk priced higher. Fiscal concerns possible.' },
    ],
    
    playbook: [
      { level: 'low', actions: ['Duration expensive', 'Consider shorter maturities', 'Credit over rates'] },
      { level: 'elevated', actions: ['Duration attractive', 'Extend maturities', 'Lock in yields'] },
    ],
    
    relatedSignals: ['REAL_10Y', 'CURVE_2S10S', 'CURVE_3M10Y'],
  },

  // ===========================================================================
  // FUNDING SIGNALS
  // ===========================================================================

  SOFR_EFFR_SPREAD: {
    whyItMatters: `SOFR (Secured Overnight Financing Rate) vs EFFR (Effective Fed Funds Rate) measures stress in repo markets. When SOFR spikes above EFFR, it signals collateral scarcity or funding stress. The September 2019 repo crisis saw this spread blow out, forcing Fed intervention.`,
    
    interpretation: [
      { level: 'normal', range: '-5 to +5 bps', meaning: 'Normal', implication: 'Repo markets functioning well.' },
      { level: 'elevated', range: '5 to 15 bps', meaning: 'Mild stress', implication: 'Temporary tightness. Monitor quarter-end.' },
      { level: 'high', range: '15 to 30 bps', meaning: 'Significant stress', implication: 'Funding concerns. Watch for Fed response.' },
      { level: 'extreme', range: '> 30 bps', meaning: 'Crisis', implication: 'Repo market dysfunction. Fed intervention likely.' },
    ],
    
    historicalEvents: [
      { date: 'Sep 2019', value: '+300 bps', event: 'Repo crisis', description: 'Sudden funding squeeze forced Fed emergency operations' },
      { date: 'Mar 2020', value: '+50 bps', event: 'COVID stress', description: 'Brief dislocation during market panic' },
    ],
    
    playbook: [
      { level: 'normal', actions: ['Normal operations', 'Repo strategies viable', 'Carry trades OK'] },
      { level: 'elevated', actions: ['Monitor funding', 'Reduce repo exposure', 'Increase liquidity buffers'] },
      { level: 'high', actions: ['Defensive funding', 'Reduce leverage', 'Cash priority'] },
      { level: 'extreme', actions: ['Crisis mode', 'Preserve liquidity', 'Avoid forced selling'] },
    ],
    
    relatedSignals: ['EFFR_IORB_SPREAD', 'FUNDING_STRESS_INDEX', 'NET_LIQUIDITY'],
  },

  EFFR_IORB_SPREAD: {
    whyItMatters: `EFFR vs IORB (Interest on Reserve Balances) shows where Fed Funds trade relative to the Fed's administered rate. Persistent deviations signal reserve distribution issues. When EFFR trades well below IORB, excess reserves may be poorly distributed across the banking system.`,
    
    interpretation: [
      { level: 'normal', range: '-2 to +2 bps', meaning: 'Normal', implication: 'Fed funds trading as expected.' },
      { level: 'elevated', range: '-5 to -2 bps or +2 to +5 bps', meaning: 'Mild deviation', implication: 'Some reserve imbalance.' },
      { level: 'high', range: 'Beyond ±5 bps', meaning: 'Significant deviation', implication: 'Reserve distribution issues.' },
    ],
    
    playbook: [
      { level: 'normal', actions: ['Normal money market operations', 'Fed policy transmitting well'] },
      { level: 'elevated', actions: ['Monitor for persistence', 'May signal liquidity issues'] },
    ],
    
    relatedSignals: ['SOFR_EFFR_SPREAD', 'NET_LIQUIDITY'],
  },

  FUNDING_STRESS_INDEX: {
    whyItMatters: `The Funding Stress Index combines money market spreads, repo rates, and bank funding costs into a single metric. Rising funding stress often precedes broader market stress, as financing conditions are the plumbing of financial markets.`,
    
    interpretation: [
      { level: 'low', range: '< 20', meaning: 'Easy funding', implication: 'Abundant liquidity. Carry strategies work.' },
      { level: 'normal', range: '20 - 45', meaning: 'Normal', implication: 'Standard funding conditions.' },
      { level: 'elevated', range: '45 - 65', meaning: 'Tightening', implication: 'Funding becoming scarce. Reduce leverage.' },
      { level: 'high', range: '65 - 80', meaning: 'Stress', implication: 'Significant funding pressure. Defensive mode.' },
      { level: 'extreme', range: '> 80', meaning: 'Crisis', implication: 'Funding markets seizing. Preservation mode.' },
    ],
    
    playbook: [
      { level: 'low', actions: ['Leverage OK', 'Carry strategies', 'Normal risk-taking'] },
      { level: 'elevated', actions: ['Reduce leverage', 'Shorten funding', 'Build cash'] },
      { level: 'high', actions: ['Deleverage', 'Liquidity priority', 'Avoid rollover risk'] },
    ],
    
    relatedSignals: ['SOFR_EFFR_SPREAD', 'NET_LIQUIDITY', 'CREDIT_STRESS_INDEX'],
  },

  // ===========================================================================
  // VOLATILITY SIGNALS
  // ===========================================================================

  VIX_TERM_SPREAD: {
    whyItMatters: `The VIX term structure (3-month VIX vs spot VIX) reveals market fear dynamics. A negative spread (backwardation) means near-term fear exceeds long-term expectations - classic panic behavior. Persistent inversion often marks market bottoms, while steep contango can signal complacency.`,
    
    interpretation: [
      { level: 'extreme', range: '< -5', meaning: 'Severe backwardation', implication: 'Panic selling. Potential capitulation. Contrarian opportunity.' },
      { level: 'high', range: '-5 to -2', meaning: 'Backwardation', implication: 'Fear elevated. Hedging demand high. Watch for reversal.' },
      { level: 'elevated', range: '-2 to 0', meaning: 'Flat', implication: 'Uncertain market. Mixed signals.' },
      { level: 'normal', range: '0 to 3', meaning: 'Mild contango', implication: 'Normal conditions. VIX term premium.' },
      { level: 'low', range: '> 3', meaning: 'Steep contango', implication: 'Complacency. Possible vol selling overcrowded.' },
    ],
    
    historicalEvents: [
      { date: 'Mar 2020', value: '-12', event: 'COVID crash', description: 'Extreme backwardation signaled capitulation' },
      { date: 'Feb 2018', value: '-8', event: 'Volmageddon', description: 'XIV blowup during vol spike' },
      { date: 'Aug 2015', value: '-7', event: 'China deval', description: 'Flash crash backwardation' },
    ],
    
    correlations: [
      { signal: 'Market Bottoms', correlation: -0.73, leadLag: 'Backwardation often marks bottoms' },
      { signal: 'VIX Level', correlation: 0.82, leadLag: 'Spikes coincide with backwardation' },
    ],
    
    playbook: [
      { level: 'extreme', actions: ['Potential buying opportunity', 'Vol selling attractive', 'Contrarian long'] },
      { level: 'high', actions: ['Reduce short vol', 'Wait for normalization', 'Selective buying'] },
      { level: 'normal', actions: ['Normal vol strategies', 'Hedging reasonable cost'] },
      { level: 'low', actions: ['Caution on vol selling', 'Cheap hedges available', 'Watch for unwind'] },
    ],
    
    relatedSignals: ['VIX_ZSCORE', 'CREDIT_STRESS_INDEX'],
  },

  VIX_ZSCORE: {
    whyItMatters: `The VIX Z-score measures current volatility relative to its 1-year history. Extreme readings (±2 standard deviations) often mark turning points. A z-score above +2 typically signals peak fear, while below -1 can indicate dangerous complacency.`,
    
    interpretation: [
      { level: 'extreme', range: '> +2.5', meaning: 'Extreme fear', implication: 'Panic. Historically good buying opportunity within 1-3 months.' },
      { level: 'high', range: '+1.5 to +2.5', meaning: 'Elevated fear', implication: 'Risk-off environment. Volatility selling risky.' },
      { level: 'elevated', range: '+0.5 to +1.5', meaning: 'Above average', implication: 'Heightened uncertainty. Active hedging appropriate.' },
      { level: 'normal', range: '-0.5 to +0.5', meaning: 'Normal', implication: 'Typical volatility. Standard positioning.' },
      { level: 'low', range: '< -0.5', meaning: 'Complacent', implication: 'Low vol environment. Hedges cheap. Watch for spike.' },
    ],
    
    historicalEvents: [
      { date: 'Mar 2020', value: '+4.2', event: 'COVID panic', description: 'Highest z-score in modern history' },
      { date: 'Aug 2015', value: '+3.1', event: 'China crash', description: 'Brief but extreme fear spike' },
      { date: 'Jan 2018', value: '-1.8', event: 'Pre-Volmageddon', description: 'Extreme complacency preceded blowup' },
    ],
    
    playbook: [
      { level: 'extreme', actions: ['Consider buying fear', 'Vol selling after spike subsides', 'Contrarian positioning'] },
      { level: 'high', actions: ['Caution', 'Reduce risk', 'Wait for normalization'] },
      { level: 'normal', actions: ['Standard allocation', 'Normal hedging', 'Full risk budget'] },
      { level: 'low', actions: ['Buy protection', 'Cheap hedges', 'Reduce complacent positions'] },
    ],
    
    relatedSignals: ['VIX_TERM_SPREAD', 'HY_IG_SPREAD', 'CREDIT_STRESS_INDEX'],
  },
};

/**
 * Get intelligence data for a signal
 */
export function getSignalIntelligence(signalId) {
  return SIGNAL_INTELLIGENCE[signalId] || null;
}

/**
 * Get the interpretation for a specific level
 */
export function getInterpretationForLevel(signalId, level) {
  const intel = SIGNAL_INTELLIGENCE[signalId];
  if (!intel?.interpretation) return null;
  return intel.interpretation.find(i => i.level === level);
}

/**
 * Get playbook actions for a specific level
 */
export function getPlaybookForLevel(signalId, level) {
  const intel = SIGNAL_INTELLIGENCE[signalId];
  if (!intel?.playbook) return null;
  const playbook = intel.playbook.find(p => p.level === level);
  return playbook?.actions || [];
}
